import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Badge } from "./common";
import { JarvisOrb } from "@/components/JarvisOrb";
import { ChevronDown, AlertTriangle } from "lucide-react";

/* Minimal assistant scene: the live orb is the focus. Only REAL data shows — no zero KPIs, no empty cards. */
export const TodayPanel = ({ orbState, onActivate, sessionActive, showOrb = true, orbSize = 420 }) => {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [prio, setPrio] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/today").then((r) => setData(r.data)).catch(() => {});
    api.get("/today/priorities").then((r) => setPrio(r.data)).catch(() => {});
  }, []);

  const c = data?.counts || {};
  const parts = [];
  if (c.open_tasks) parts.push(`${c.open_tasks} açık görev`);
  if (data?.overdue?.length) parts.push(`${data.overdue.length} geciken`);
  if (data?.due_today?.length) parts.push(`${data.due_today.length} bugün`);
  if (data?.pending_client_replies?.length) parts.push(`${data.pending_client_replies.length} yanıt bekleyen teklif`);
  if (data?.active_projects?.length) parts.push(`${data.active_projects.length} aktif proje`);
  const hasData = parts.length > 0;

  return (
    <div className="flex flex-col items-center text-center" data-testid="panel-today">
      {showOrb && <JarvisOrb state={orbState} size={orbSize} onActivate={onActivate} active={sessionActive} />}

      {data && hasData && (        <div className="mt-5 max-w-xl" data-testid="today-summary">
          <button onClick={() => setOpen((v) => !v)} aria-expanded={open} data-testid="today-summary-toggle"
            className="inline-flex items-center gap-2 text-sm text-white/75 hover:text-cyan-200">
            {data.overdue?.length ? <AlertTriangle size={14} className="text-amber-300" /> : null}
            {parts.join(" · ")}
            <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
          {open && (
            <div className="mt-4 grid sm:grid-cols-2 gap-3 text-left stagger" data-testid="today-details">
              {prio?.priorities?.length > 0 && (
                <div className="sm:col-span-2" data-testid="today-priorities">
                  <div className="label-eyebrow mb-2">Bugün öncelikli 3 iş</div>
                  <div className="space-y-2">
                    {prio.priorities.map((p, i) => (
                      <div key={i} className="panel flex items-center justify-between px-3 py-2 text-sm" data-testid="priority-row">
                        <span className="truncate text-white/90">{i + 1}. {p.label}</span>
                        <Badge color={p.reason.includes("gecik") ? "red" : "cyan"}>{p.reason}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {data.overdue?.map((x) => <Row key={x.task_id} left={x.title} right={<Badge color="red">{x.due_date}</Badge>} />)}
              {data.top_tasks?.map((x) => <Row key={x.task_id} left={x.title} right={<Badge color={x.priority === "high" ? "red" : "cyan"}>{x.priority}</Badge>} />)}
              {data.pending_client_replies?.map((x, i) => <Row key={`p${i}`} left={`${x.number} · ${x.client || "—"}`} right={<Badge color="amber">£{x.total}</Badge>} />)}
              {data.active_projects?.map((x, i) => <Row key={`a${i}`} left={x.name} right={<Badge color="blue">{x.progress}%</Badge>} />)}
            </div>
          )}
        </div>
      )}
      {data && !hasData && null /* quiet scene: no filler text, the orb speaks for itself */}
    </div>
  );
};

const Row = ({ left, right }) => (
  <div className="panel flex items-center justify-between px-3 py-2 text-sm">
    <span className="truncate text-white/85">{left}</span>{right}
  </div>
);
